import {
  FINDING_STATUS_LABELS,
  HANDOVER_LIMITS,
  HANDOVER_STATUS_LABELS,
  HANDOVER_TYPE_LABELS,
  INSPECTION_STATUS_LABELS,
  type HandoverCatalogsResponse,
  type HandoverDocument,
  type HandoverPhoto,
  type InspectionItem,
  type InspectionStatus,
  type ClosingProblem,
  type ReinspectionResult,
  handoverCatalogsResponseSchema,
  handoverDocumentSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useEffect, useEffectEvent, useId, useRef, useState } from 'react';
import { ApiError, NetworkError, apiDelete, apiGet, apiPost, apiPut } from '../api/client';
import { Modal } from '../master-data/Modal';
import { Value } from '../master-data/Value';
import {
  confirmDiscardPending,
  reportPendingChanges,
  warnOnUnload,
} from '../master-data/unsaved-changes';
import { useLoad } from '../master-data/use-master-data';
import { useFieldMode } from '../master-data/use-narrow-screen';
import { DateSheet, FutureDateWarning, isFuture } from './DateSheet';
import { ContractorContact, FindingForm } from './FindingForm';
import { type MergeResult, adoptSavedIds, mergeDrafts, resolveConflicts } from './draft-merge';
import {
  type OpenFinding,
  findDraft,
  findOpenFinding,
  forgetDraft,
  forgetOpenFinding,
  keepDraft,
  keepOpenFinding,
} from './draft-store';
import { CHECKS_SECTION_ID, GeneralChecksPanel, checkElementId } from './GeneralChecksPanel';
import { InspectionReview } from './InspectionReview';
import {
  REINSPECTION_SECTION_ID,
  ReinspectionForm,
  ReinspectionPanel,
  reinspectionElementId,
} from './ReinspectionPanel';
import { type ReviewTarget, reviewOf } from './review';
import { type Closing, InspectionPanel, ResolveForm } from './InspectionPanel';
import { type HandoverLocation, type HandoverStep, formatStep } from './location';
import { navigate } from '../routing';
import { MobileHandover, type SaveView } from './MobileHandover';
import { DiscardDraftDialog, DiscardedNotice } from './DiscardDraft';
import { RoomsDialog } from './Rooms';
import { ClosingPanel } from './ClosingPanel';
import { Icon } from '../ui/Icon';
import { PendingPhotos } from './PendingPhotos';
import { PhotoStrip, PhotoViewer } from './PhotoViewer';
import { photoUrl } from './prepare-photo';
import { dateOf, timeOf } from '../schedule/calendar';
import { LoadingBlock, Problem, apartmentTitle } from './common';
import { PropertySummary } from './PropertySummary';
import { findingFromItem } from './handover-draft';
import {
  type Draft,
  type DraftFinding,
  type DraftMeter,
  type DraftReinspection,
  type FindingFields,
  METER_SUGGESTIONS,
  type MeterErrors,
  closeFindings,
  draftErrors,
  draftFrom,
  formatDate,
  groupByArea,
  inspectionConflicts,
  isDirty,
  newKey,
  openFindingsByItem,
  toSaveRequest,
  today,
  validateMeter,
} from './handover-draft';

/**
 * SAFE-DRAFT: the draft saves itself shortly after each change (when it is valid); `offline` and
 * `error` wait for a retry (or the connection coming back) and keep the changes on this device.
 */
type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved' }
  | { readonly kind: 'offline' }
  | { readonly kind: 'error'; readonly text: string };

/** Another user saved meanwhile and both changed the same things: the user chooses. */
interface Merging {
  readonly result: MergeResult;
  readonly latest: HandoverDocument;
}

/** Quiet time after the last change before saving automatically. */
const AUTOSAVE_MS = 1500;

/** Leaving the editor keeps unsaved changes on this device (they are offered again on return). */
const KEPT_ON_LEAVE =
  'יש שינויים שעדיין לא נשמרו בשרת. הם שמורים בדפדפן זה ויוצעו לשחזור כשתחזרו למסירה. לעזוב?';

/** The things both the user and another user changed: the user's change is kept unless unticked. */
function MergeForm({
  merging,
  onDone,
}: {
  readonly merging: Merging;
  readonly onDone: (keepMine: ReadonlySet<string>) => void;
}) {
  const [mine, setMine] = useState<ReadonlySet<string>>(
    () => new Set(merging.result.conflicts.map((c) => c.id)),
  );
  const id = useId();
  return (
    <form
      className="merge-form"
      onSubmit={(e) => {
        e.preventDefault();
        onDone(mine);
      }}
    >
      <p>
        משתמש אחר שמר שינויים בטיוטה בזמן שעבדת.{' '}
        {merging.result.applied > 0 &&
          `${String(merging.result.applied)} מהשינויים שלך שולבו בגרסה שלו. `}
        בפריטים הבאים שניכם שיניתם את אותו דבר: בחרו איזה שינוי לשמור.
      </p>
      {merging.result.conflicts.map((c, index) => (
        <fieldset key={c.id} className="merge-form__item">
          <legend>{c.label}</legend>
          {(['mine', 'theirs'] as const).map((side) => (
            <label key={side} className="choice">
              <input
                type="radio"
                name={`${id}-${String(index)}`}
                checked={(side === 'mine') === mine.has(c.id)}
                onChange={() => {
                  setMine((set) => {
                    const next = new Set(set);
                    if (side === 'mine') next.add(c.id);
                    else next.delete(c.id);
                    return next;
                  });
                }}
              />
              <span>
                {side === 'mine' ? 'השינוי שלי' : 'השינוי של המשתמש האחר'}:{' '}
                <b>{side === 'mine' ? c.mine : c.theirs}</b>
              </span>
            </label>
          ))}
        </fieldset>
      ))}
      <div className="form-actions">
        <button type="submit" className="button button--primary">
          המשך ושמירה
        </button>
      </div>
    </form>
  );
}

/**
 * The draft to start from: the document, or the changes kept on this device for it (after an
 * expired sign-in, a refresh or a network outage), brought onto the document's version.
 */
function startingDraft(doc: HandoverDocument): {
  draft: Draft;
  notice: string | null;
  merging: Merging | null;
} {
  const kept = doc.permissions.edit ? findDraft(doc.id) : null;
  if (kept === null) return { draft: draftFrom(doc), notice: null, merging: null };
  const at = timeOf(new Date(kept.savedAt).toISOString());
  if (kept.base.rowVersion === doc.rowVersion) {
    if (!isDirty(kept.draft, doc)) {
      forgetDraft(doc.id);
      return { draft: draftFrom(doc), notice: null, merging: null };
    }
    return {
      draft: kept.draft,
      notice: `שוחזרו שינויים שלא נשמרו (מ־${at}). הם נשמרים עכשיו.`,
      merging: null,
    };
  }
  const result = mergeDrafts(kept.base, kept.draft, doc);
  if (result.conflicts.length > 0)
    return { draft: result.draft, notice: null, merging: { result, latest: doc } };
  return {
    draft: result.draft,
    notice:
      result.applied === 0
        ? null
        : `שוחזרו שינויים שלא נשמרו (מ־${at}) ושולבו בגרסה העדכנית של הטיוטה. הם נשמרים עכשיו.`,
    merging: null,
  };
}

/** A Hebrew explanation of a refused save. */
function saveError(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = (error.body['fields'] ?? {}) as Record<string, string>;
    if (Object.values(fields).includes('reinspected_finding_removed'))
      return 'לא ניתן להסיר ליקוי שנבדק מחדש במסירה הסופית. ניתן לסגור אותו (תוקן או נרשם בטעות).';
    if (Object.keys(fields).some((f) => f.includes('photoIds')))
      return 'תמונה שהועלתה אינה זמינה עוד בשרת. הסירו אותה מהליקוי ונסו שוב.';
    if (error.status === 422) return 'השרת דחה חלק מהנתונים. בדקו את השדות ונסו שוב.';
    if (error.status === 403) return 'אין לך הרשאה לערוך מסירה זו. השינויים שמורים במכשיר זה.';
    if (error.status === 401) return 'פג תוקף ההתחברות. התחברו מחדש כדי להמשיך; השינויים שמורים.';
  }
  return 'השמירה נכשלה. השינויים שמורים במכשיר זה; נסו שוב.';
}

type Editing =
  | {
      readonly mode: 'add';
      readonly item?: InspectionItem;
      /** Started from a photo in a room: the room, and the photos to upload first. */
      readonly room?: { readonly areaId: string; readonly area: string };
      readonly files?: readonly File[];
    }
  | { readonly mode: 'edit'; readonly key: string }
  | null;

/** Findings being closed: to mark `item` with `target`, or one finding on its own (`item` null). */
type Resolving = {
  readonly findings: readonly DraftFinding[];
  readonly item: {
    readonly id: string;
    readonly name: string;
    readonly target: InspectionStatus;
  } | null;
} | null;

const FINDINGS_SECTION_ID = 'handover-findings';

function Findings({
  draft,
  savedPhotoIds,
  readOnly,
  catalogs,
  urlOf,
  onAdd,
  onEdit,
  onRemove,
  onClose,
}: {
  readonly draft: Draft;
  /** Photos the server has attached (others are uploaded but wait for the next save). */
  readonly savedPhotoIds: ReadonlySet<string>;
  readonly readOnly: boolean;
  readonly catalogs: HandoverCatalogsResponse;
  readonly urlOf: (photo: HandoverPhoto) => string;
  readonly onAdd: () => void;
  readonly onEdit: (key: string) => void;
  readonly onRemove: (finding: DraftFinding) => void;
  /** Findings of inspection items are closed, never removed. */
  readonly onClose: (finding: DraftFinding) => void;
}) {
  const groups = groupByArea(draft.findings);
  const titleId = useId();
  const [viewing, setViewing] = useState<{ key: string; index: number } | null>(null);
  const viewed = draft.findings.find((f) => f.key === viewing?.key);
  return (
    <section
      id={FINDINGS_SECTION_ID}
      className="editor-section"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <div className="editor-section__head">
        <h2 id={titleId} className="section-title">
          ליקויים שנמצאו בבדיקה <span className="count">({draft.findings.length})</span>
        </h2>
        {!readOnly && (
          <button type="button" className="button button--primary" onClick={onAdd}>
            + הוספת ליקוי
          </button>
        )}
      </div>
      {groups.length === 0 ? (
        <p className="empty">לא נרשמו ליקויים.</p>
      ) : (
        groups.map((group) => (
          <section key={group.area} className="area-group" aria-label={group.area}>
            <h3 className="area-group__title">
              {group.area} <span className="count">({group.items.length})</span>
            </h3>
            <ul className="finding-list">
              {group.items.map((f) => (
                <li key={f.key} className={`finding finding--${f.status}`}>
                  <div className="finding__body">
                    <p className="finding__component">{f.component}</p>
                    <p className="finding__description">{f.description}</p>
                    <p className="finding__meta">
                      <span>
                        נבדק <Value text={formatDate(f.inspectedOn)} />
                      </span>
                      {f.workCategory.trim() !== '' && <span>תחום: {f.workCategory}</span>}
                      {f.contractor.trim() !== '' && <span>קבלן / ספק: {f.contractor}</span>}
                      {f.inspectionItemId !== null && <span>מרשימת הבדיקה</span>}
                      {(f.inspectionItemId !== null || f.status !== 'open') && (
                        <span className={`chip chip--finding-${f.status}`}>
                          {FINDING_STATUS_LABELS[f.status]}
                        </span>
                      )}
                      {f.contractorSource === 'default' && f.contractor.trim() !== '' && (
                        <span className="hint">קבלן ברירת המחדל של התחום</span>
                      )}
                      {f.id === null && <span className="chip chip--new">חדש</span>}
                      <PendingPhotos photos={f.photos} saved={savedPhotoIds} />
                    </p>
                    <ContractorContact catalogs={catalogs} contractorId={f.contractorId} />
                    <PhotoStrip
                      photos={f.photos}
                      urlOf={urlOf}
                      label={`תמונות: ${f.component}`}
                      onOpen={(index) => {
                        setViewing({ key: f.key, index });
                      }}
                    />
                  </div>
                  {!readOnly && (
                    <div className="finding__actions">
                      <button
                        type="button"
                        className="button button--small"
                        aria-label={`עריכת ליקוי: ${f.component}`}
                        onClick={() => {
                          onEdit(f.key);
                        }}
                      >
                        עריכה
                      </button>
                      {f.inspectionItemId === null ? (
                        <button
                          type="button"
                          className="button button--small button--danger"
                          aria-label={`הסרת ליקוי: ${f.component}`}
                          onClick={() => {
                            onRemove(f);
                          }}
                        >
                          הסרה
                        </button>
                      ) : (
                        f.status === 'open' && (
                          <button
                            type="button"
                            className="button button--small"
                            aria-label={`סגירת ליקוי: ${f.component}`}
                            onClick={() => {
                              onClose(f);
                            }}
                          >
                            סגירה
                          </button>
                        )
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      <PhotoViewer
        photos={viewed?.photos ?? []}
        index={viewed === undefined ? null : (viewing?.index ?? null)}
        urlOf={urlOf}
        onIndex={(index) => {
          setViewing((v) => (v === null ? null : { ...v, index }));
        }}
        onClose={() => {
          setViewing(null);
        }}
      />
    </section>
  );
}

function Meters({
  meters,
  readOnly,
  showErrors,
  onChange,
  onAdd,
  onRemove,
}: {
  readonly meters: readonly DraftMeter[];
  readonly readOnly: boolean;
  readonly showErrors: boolean;
  readonly onChange: (key: string, field: keyof DraftMeter, value: string) => void;
  readonly onAdd: () => void;
  readonly onRemove: (key: string) => void;
}) {
  const titleId = useId();
  const listId = useId();
  return (
    <section className="editor-section" aria-labelledby={titleId}>
      <div className="editor-section__head">
        <h2 id={titleId} className="section-title">
          קריאת מונים <span className="count">({meters.length})</span>
        </h2>
        {!readOnly && meters.length < HANDOVER_LIMITS.meters && (
          <button type="button" className="button" onClick={onAdd}>
            + הוספת מונה
          </button>
        )}
      </div>
      <datalist id={listId}>
        {METER_SUGGESTIONS.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {meters.length === 0 ? (
        <p className="empty">לא נרשמו קריאות מונים.</p>
      ) : (
        <ul className="meter-list">
          {meters.map((m, index) => {
            const errors: MeterErrors = showErrors ? validateMeter(m) : {};
            const input = (
              field: 'meterType' | 'meterNumber' | 'reading' | 'note',
              label: string,
              extra: { list?: string; inputMode?: 'decimal'; dir?: 'ltr' } = {},
            ) => (
              <label className={`field meter__${field}`}>
                <span>{label}</span>
                <input
                  value={m[field]}
                  disabled={readOnly}
                  autoComplete="off"
                  aria-invalid={errors[field] !== undefined}
                  {...extra}
                  onChange={(e) => {
                    onChange(m.key, field, e.target.value);
                  }}
                />
                {errors[field] !== undefined && <p className="field__error">{errors[field]}</p>}
              </label>
            );
            return (
              <li key={m.key} className="meter" aria-label={`מונה ${String(index + 1)}`}>
                {input('meterType', 'סוג מונה', { list: listId })}
                {input('meterNumber', 'מספר מונה', { dir: 'ltr' })}
                {input('reading', 'קריאה', { inputMode: 'decimal', dir: 'ltr' })}
                {input('note', 'הערה')}
                {!readOnly && (
                  <button
                    type="button"
                    className="button button--small button--danger meter__remove"
                    aria-label={`הסרת מונה ${String(index + 1)}`}
                    onClick={() => {
                      onRemove(m.key);
                    }}
                  >
                    הסרה
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** The finding dialog to reopen for a finding left open in it (Gate 2A), if it still applies. */
function reopenedDialog(open: OpenFinding | null, doc: HandoverDocument, draft: Draft): Editing {
  if (open === null) return null;
  if (open.mode === 'edit')
    return open.key !== null && draft.findings.some((f) => f.key === open.key)
      ? { mode: 'edit', key: open.key }
      : null;
  if (open.itemId === null) return { mode: 'add' };
  const item = doc.inspection.items.find((i) => i.id === open.itemId);
  return item === undefined ? null : { mode: 'add', item };
}

function EditorBody({
  initialDoc,
  catalogs,
  location,
  onBack,
  onDiscarded,
}: {
  readonly initialDoc: HandoverDocument;
  readonly catalogs: HandoverCatalogsResponse;
  readonly location: HandoverLocation;
  readonly onBack: () => void;
  /** The draft was discarded here: its local copy is gone, and the editor closes. */
  readonly onDiscarded: (doc: HandoverDocument) => void;
}) {
  // Phones and touch tablets get the field layout (one room at a time, large targets).
  const narrow = useFieldMode();
  const [start] = useState(() => {
    const draftStart = startingDraft(initialDoc);
    const kept = initialDoc.permissions.edit ? findOpenFinding(initialDoc.id) : null;
    const dialog = reopenedDialog(kept, initialDoc, draftStart.draft);
    if (dialog === null && kept !== null) forgetOpenFinding(initialDoc.id);
    return {
      ...draftStart,
      dialog,
      fields: dialog === null ? null : (kept?.fields ?? null),
      notice:
        dialog === null
          ? draftStart.notice
          : [
              draftStart.notice,
              'שוחזר ליקוי שהתחלתם לרשום והוא עדיין לא נוסף לטיוטה. השלימו אותו והוסיפו לטיוטה.',
            ]
              .filter((t) => t !== null)
              .join(' '),
    };
  });
  /** The latest version saved on the server, which the draft is based on. */
  const [doc, setDoc] = useState(initialDoc);
  const [draft, setDraft] = useState<Draft>(start.draft);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [merging, setMerging] = useState<Merging | null>(start.merging);
  const [editing, setEditing] = useState<Editing>(start.dialog);
  /** A finding restored into its dialog after a sign-in or a refresh, until the dialog closes. */
  const [restoredFields, setRestoredFields] = useState<FindingFields | null>(start.fields);
  /** The open finding dialog holds something not yet added to the draft. */
  const [findingTyped, setFindingTyped] = useState(start.fields !== null);
  const [choosingDate, setChoosingDate] = useState(false);
  const [resolving, setResolving] = useState<Resolving>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** What happened to the user's unsaved changes (restored, merged): shown above the editor. */
  const [banner, setBanner] = useState<string | null>(start.notice);
  const [starting, setStarting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [focusRoom, setFocusRoom] = useState<{ readonly areaId: string } | null>(null);
  /** The initial finding whose reinspection is being edited in the dialog. */
  const [reinspecting, setReinspecting] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [editingRooms, setEditingRooms] = useState(false);
  const readOnly = !doc.permissions.edit;
  const dirty = isDirty(draft, doc);
  const dateId = useId();
  const items = doc.inspection.items;
  const itemName = (id: string) => items.find((i) => i.id === id)?.name ?? '';
  const invalid = Object.keys(draftErrors(draft)).length > 0;
  const mismatched = inspectionConflicts(draft);
  const valid = !invalid && mismatched.length === 0;
  // The latest draft and document, for saves that finish after more changes were made.
  const latest = useRef({ draft, doc });
  const busy = useRef(false);
  useEffect(() => {
    latest.current = { draft, doc };
  }, [draft, doc]);

  /** Takes a document the server returned (after starting checks or linking): a fresh draft. */
  const adopt = (next: HandoverDocument) => {
    latest.current = { draft: draftFrom(next), doc: next };
    setDoc(next);
    setDraft(draftFrom(next));
  };

  /** Another user saved meanwhile: bring the user's changes onto the newer version. */
  const merge = async () => {
    const current = latest.current;
    const newer = await apiGet(
      `/projects/${current.doc.project.id}/handovers/${current.doc.id}`,
      handoverDocumentSchema,
    );
    const result = mergeDrafts(current.doc, latest.current.draft, newer);
    if (result.conflicts.length > 0) {
      setMerging({ result, latest: newer });
      setSave({ kind: 'idle' });
      return;
    }
    latest.current = { draft: result.draft, doc: newer };
    setDoc(newer);
    setDraft(result.draft);
    setSave({ kind: 'idle' });
    setBanner(
      'משתמש אחר שמר שינויים בטיוטה בזמן שעבדת. השינויים שלך שולבו בגרסה שלו ונשמרים עכשיו.',
    );
  };

  /** Saves the draft as it is now (automatically, or by the user's request). */
  const submit = async (manual: boolean) => {
    if (busy.current || merging !== null || readOnly) return;
    const { draft: sending, doc: base } = latest.current;
    if (!isDirty(sending, base)) return;
    if (Object.keys(draftErrors(sending)).length > 0) {
      if (manual) {
        setShowErrors(true);
        setSave({ kind: 'error', text: 'יש שדות שדורשים תיקון. השינויים שמורים במכשיר זה.' });
      }
      return;
    }
    const conflicts = inspectionConflicts(sending);
    if (conflicts.length > 0) {
      if (manual)
        setSave({
          kind: 'error',
          text: `תוצאת הבדיקה אינה תואמת את הליקויים בפריטים: ${conflicts.map(itemName).join(', ')}. פריט "לא תקין" צריך ליקוי פתוח, ופריט עם ליקוי פתוח אינו יכול להיות תקין.`,
        });
      return;
    }
    busy.current = true;
    setSave({ kind: 'saving' });
    try {
      const saved = await apiPut(
        `/projects/${base.project.id}/handovers/${base.id}`,
        toSaveRequest(sending, base.rowVersion, base.inspection.items),
        handoverDocumentSchema,
      );
      const next = adoptSavedIds(sending, latest.current.draft, saved);
      latest.current = { draft: next, doc: saved };
      setDoc(saved);
      setDraft(next);
      setShowErrors(false);
      setSave({ kind: 'saved' });
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.body['conflict'] as { code?: string } | undefined)?.code === 'finding_released'
      )
        setSave({
          kind: 'error',
          text: 'ליקוי זה שוחרר לטיפול קבלן, ולכן הוא ותמונותיו נשמרים. כדי למחוק אותו, בטלו קודם את השחרור בניהול ליקויים.',
        });
      else if (error instanceof ApiError && error.status === 409)
        await merge().catch((failure: unknown) => {
          setSave(
            failure instanceof NetworkError
              ? { kind: 'offline' }
              : { kind: 'error', text: saveError(failure) },
          );
        });
      else if (error instanceof NetworkError) setSave({ kind: 'offline' });
      else setSave({ kind: 'error', text: saveError(error) });
    } finally {
      busy.current = false;
    }
  };
  const autosave = useEffectEvent(() => {
    void submit(false);
  });
  // Saves shortly after the last change, when the draft is complete enough to save.
  useEffect(() => {
    if (readOnly || !dirty || !valid || merging !== null) return;
    if (save.kind === 'saving' || save.kind === 'offline' || save.kind === 'error') return;
    const timer = window.setTimeout(autosave, AUTOSAVE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [draft, doc, dirty, valid, merging, readOnly, save.kind]);
  // Retries when the connection comes back.
  const reconnected = useEffectEvent(() => {
    if (save.kind === 'offline') void submit(false);
  });
  useEffect(() => {
    window.addEventListener('online', reconnected);
    return () => {
      window.removeEventListener('online', reconnected);
    };
  }, []);
  // Unsaved changes are kept on this device until the server has them.
  useEffect(() => {
    if (readOnly) return;
    if (dirty) keepDraft(doc, draft);
    else forgetDraft(doc.id);
  }, [doc, draft, dirty, readOnly]);

  useEffect(() => {
    reportPendingChanges(dirty || findingTyped ? 1 : 0, KEPT_ON_LEAVE);
    window.addEventListener('beforeunload', warnOnUnload);
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload);
      reportPendingChanges(0);
    };
  }, [dirty, findingTyped]);

  const photosPath = `/projects/${doc.project.id}/handovers/${doc.id}/photos`;
  const urlOf = (photo: HandoverPhoto) => photoUrl(doc.project.id, doc.id, photo.id);
  // Photos uploaded in the open dialog: removed again if the dialog does not keep them.
  const dialogUploads = useRef<string[]>([]);
  const discardUploads = (keep: readonly HandoverPhoto[]) => {
    for (const photoId of dialogUploads.current) {
      if (!keep.some((p) => p.id === photoId))
        void apiDelete(`${photosPath}/${photoId}`).catch(() => undefined);
    }
    dialogUploads.current = [];
  };
  const change = (next: (d: Draft) => Draft) => {
    setDraft(next);
    if (save.kind === 'saved' || save.kind === 'error' || save.kind === 'offline')
      setSave({ kind: 'idle' });
  };

  const editingFinding =
    editing?.mode === 'edit' ? draft.findings.find((f) => f.key === editing.key) : undefined;
  const blankFinding: FindingFields = {
    areaId: null,
    area: '',
    componentId: null,
    component: '',
    contractorId: null,
    contractor: '',
    workCategoryId: null,
    workCategory: '',
    description: '',
    inspectedOn: draft.handoverDate === '' ? today() : draft.handoverDate,
    photos: [],
    inspectionItemId: null,
    status: 'open',
    resolutionNote: '',
    contractorSource: null,
  };
  /** A new finding on an inspection item: area, component, work category from the item (DR-189). */
  const blankFor = (item: InspectionItem): FindingFields => ({
    ...blankFinding,
    ...findingFromItem(item),
  });
  const editingItem =
    editing?.mode === 'add'
      ? editing.item
      : items.find((i) => i.id === editingFinding?.inspectionItemId);

  const setResult = (id: string, status: InspectionStatus) => {
    change((d) => ({ ...d, results: { ...d.results, [id]: status } }));
  };
  const mark = (item: InspectionItem, status: Exclude<InspectionStatus, 'unchecked'>) => {
    setNotice(null);
    if (status === 'defect') {
      // A defect is recorded with its finding; the result changes when the finding is added.
      setEditing({ mode: 'add', item });
      return;
    }
    const open = openFindingsByItem(draft.findings).get(item.id) ?? [];
    if (open.length > 0)
      setResolving({ findings: open, item: { id: item.id, name: item.name, target: status } });
    else setResult(item.id, status);
  };
  const allOk = (list: readonly InspectionItem[], area: string, category: string) => {
    const open = openFindingsByItem(draft.findings);
    const marked = list.filter((i) => (draft.results[i.id] ?? i.status) === 'unchecked');
    const blocked = list.filter((i) => open.has(i.id));
    change((d) => ({
      ...d,
      results: { ...d.results, ...Object.fromEntries(marked.map((i) => [i.id, 'ok' as const])) },
    }));
    setNotice(
      [
        `${category} ב${area}: ${marked.length === 1 ? 'פריט אחד סומן' : `${String(marked.length)} פריטים סומנו`} כתקינים.`,
        blocked.length > 0
          ? `${blocked.map((i) => i.name).join(', ')} נשארו "לא תקין": יש בהם ליקויים פתוחים, ויש לסגור אותם קודם.`
          : '',
        list.length - marked.length - blocked.length > 0
          ? 'פריטים שכבר סומנו (למשל "לא רלוונטי") לא שונו.'
          : '',
      ]
        .filter((t) => t !== '')
        .join(' '),
    );
  };
  const resolve = (closing: readonly Closing[]) => {
    if (resolving === null) return;
    const result = closeFindings(
      draft,
      closing,
      resolving.item === null ? null : { id: resolving.item.id, target: resolving.item.target },
    );
    change(() => result.draft);
    setNotice(
      result.reopened.length > 0
        ? `לפריט ${result.reopened.map(itemName).join(', ')} לא נותרו ליקויים פתוחים, והוא חזר ל"טרם נבדק". יש לבדוק אותו שוב.`
        : null,
    );
    setResolving(null);
  };
  const reopen = (finding: DraftFinding) => {
    change((d) => ({
      ...d,
      findings: d.findings.map((f) =>
        f.key === finding.key ? { ...f, status: 'open', resolutionNote: '' } : f,
      ),
      results:
        finding.inspectionItemId === null
          ? d.results
          : { ...d.results, [finding.inspectionItemId]: 'defect' },
    }));
  };
  const startInspection = async () => {
    setStarting(true);
    try {
      const started = await apiPost(
        `/projects/${doc.project.id}/handovers/${doc.id}/inspection`,
        { rowVersion: doc.rowVersion },
        handoverDocumentSchema,
      );
      adopt(started);
    } catch (error) {
      if (error instanceof ApiError && error.status === 422)
        setSave({
          kind: 'error',
          text: 'לפרויקט אין עדיין תבנית בדיקה מתאימה לדירה. ניתן להגדיר אותה בניהול נתוני אב ← הגדרות מסירות ← תבנית בדיקה.',
        });
      else if (error instanceof ApiError && error.status === 409)
        await merge().catch(() => undefined);
      else if (error instanceof NetworkError)
        setSave({ kind: 'error', text: 'אין חיבור לשרת. הבדיקה לא התחילה; נסו שוב.' });
      else setSave({ kind: 'error', text: 'התחלת הבדיקה נכשלה. נסו שוב.' });
    } finally {
      setStarting(false);
    }
  };

  /** Adds the general checks (draft opened before the project had any), or links the initial handover. */
  const act = async (path: string, body: object, failure: string, refused: string) => {
    setStarting(true);
    try {
      adopt(await apiPost(path, body, handoverDocumentSchema));
    } catch (error) {
      if (error instanceof ApiError && error.status === 422)
        setSave({ kind: 'error', text: refused });
      else if (error instanceof ApiError && error.status === 409)
        await merge().catch(() => undefined);
      else if (error instanceof NetworkError)
        setSave({ kind: 'error', text: `אין חיבור לשרת. ${failure}` });
      else setSave({ kind: 'error', text: failure });
    } finally {
      setStarting(false);
    }
  };
  const handoverPath = `/projects/${doc.project.id}/handovers/${doc.id}`;
  const startChecks = () =>
    act(
      `${handoverPath}/general-checks`,
      { rowVersion: doc.rowVersion },
      'הוספת הבדיקות הכלליות נכשלה. נסו שוב.',
      'לפרויקט אין עדיין בדיקות כלליות. ניתן להגדיר אותן בניהול נתוני אב ← הגדרות מסירות.',
    );
  const linkInitial = (initialHandoverId: string) =>
    act(
      `${handoverPath}/initial-link`,
      { rowVersion: doc.rowVersion, initialHandoverId },
      'הקישור למסירה הראשונית נכשל. נסו שוב.',
      'לא ניתן לקשר מסירה זו.',
    );

  const reinspectionOf = (findingId: string): DraftReinspection =>
    draft.reinspections[findingId] ?? { result: 'pending', inspectedOn: '', note: '', photos: [] };
  const setReinspection = (findingId: string, value: DraftReinspection) => {
    change((d) => ({ ...d, reinspections: { ...d.reinspections, [findingId]: value } }));
  };
  const markReinspection = (findingId: string, result: ReinspectionResult) => {
    const current = reinspectionOf(findingId);
    setReinspection(findingId, {
      ...current,
      result,
      inspectedOn:
        result === 'pending'
          ? ''
          : current.inspectedOn === ''
            ? draft.handoverDate === ''
              ? today()
              : draft.handoverDate
            : current.inspectedOn,
    });
  };
  const reinspectedFinding = doc.reinspection?.items.find(
    (r) => r.finding.id === reinspecting,
  )?.finding;

  const review = reviewOf(doc, draft);
  const goTo = (target: ReviewTarget) => {
    setReviewing(false);
    // After the dialog has closed and returned focus to its opener.
    window.setTimeout(() => {
      const focus = (id: string) => {
        const element = document.getElementById(id);
        element?.scrollIntoView({ block: 'center' });
        element?.focus();
      };
      if (target.kind === 'room') setFocusRoom({ areaId: target.areaId });
      else if (target.kind === 'check') focus(checkElementId(target.id));
      else if (target.kind === 'reinspection') focus(reinspectionElementId(target.findingId));
      else if (target.kind === 'date') focus(dateId);
      else
        focus(
          target.section === 'checks'
            ? CHECKS_SECTION_ID
            : target.section === 'reinspection'
              ? REINSPECTION_SECTION_ID
              : FINDINGS_SECTION_ID,
        );
    }, 0);
  };

  const savedPhotoIds = new Set([
    ...doc.findings.flatMap((f) => f.photos.map((p) => p.id)),
    ...(doc.reinspection?.items ?? []).flatMap((r) => r.photos.map((p) => p.id)),
  ]);
  const finishMerge = (m: Merging, keepMine: ReadonlySet<string>) => {
    const next = resolveConflicts(m.result, keepMine);
    latest.current = { draft: next, doc: m.latest };
    setDoc(m.latest);
    setDraft(next);
    setMerging(null);
    setBanner('השינויים שבחרת נשמרים עכשיו. שינויים שלא סומנו נשארו כפי שהמשתמש האחר שמר אותם.');
  };

  const status =
    merging !== null
      ? 'יש לבחור אילו שינויים לשמור'
      : save.kind === 'saving'
        ? 'שומר שינויים…'
        : save.kind === 'offline'
          ? 'אין חיבור לשרת. השינויים שמורים במכשיר זה ויישמרו כשהחיבור יחזור.'
          : save.kind === 'error'
            ? 'השמירה נכשלה. השינויים שמורים במכשיר זה.'
            : !dirty
              ? 'נשמר'
              : !valid
                ? 'שינויים שלא נשמרו: יש פרטים להשלמה לפני השמירה.'
                : 'שינויים שלא נשמרו';
  const statusKind =
    save.kind === 'offline' || save.kind === 'error'
      ? 'failed'
      : save.kind === 'saving'
        ? 'saving'
        : dirty || merging !== null
          ? 'dirty'
          : 'saved';
  /** The same state for the phone's header: short words, and what to do when it matters. */
  const saveView: SaveView =
    merging !== null
      ? { kind: 'merging', label: 'יש לבחור', detail: 'משתמש אחר שמר שינויים: יש לבחור מה לשמור.' }
      : save.kind === 'saving'
        ? { kind: 'saving', label: 'שומר…', detail: null }
        : save.kind === 'offline'
          ? {
              kind: 'offline',
              label: 'אין חיבור',
              detail: 'אין חיבור לשרת. השינויים שמורים במכשיר זה ויישמרו כשהחיבור יחזור.',
            }
          : save.kind === 'error'
            ? { kind: 'error', label: 'לא נשמר', detail: save.text }
            : !dirty
              ? { kind: 'saved', label: 'נשמר', detail: null }
              : !valid
                ? {
                    kind: 'invalid',
                    label: 'לא נשמר',
                    detail: 'שינויים שלא נשמרו: יש פרטים להשלמה לפני השמירה.',
                  }
                : { kind: 'dirty', label: 'לא נשמר עדיין', detail: null };

  const discardChanges = () => {
    void askConfirm({
      title: 'לחזור לגרסה השמורה?',
      message: 'השינויים שלא נשמרו יבוטלו, והטיוטה תחזור למצב שנשמר לאחרונה בשרת.',
      confirmLabel: 'חזרה לגרסה השמורה',
      danger: true,
    }).then((yes) => {
      if (!yes) return;
      setDraft(draftFrom(doc));
      forgetDraft(doc.id);
      setShowErrors(false);
      setSave({ kind: 'idle' });
    });
  };
  const leave = () => {
    void confirmDiscardPending().then((ok) => {
      if (ok) onBack();
    });
  };
  const roomsDialog = !readOnly && doc.inspection.templateRevision !== null && (
    <RoomsDialog
      doc={doc}
      findings={draft.findings}
      dirty={dirty}
      open={editingRooms}
      onClose={() => {
        setEditingRooms(false);
      }}
      onChanged={adopt}
    />
  );
  // Each blocker of the closing checklist leads straight to where it is fixed.
  const firstRoom = review.incomplete.find((e) => e.target.kind === 'room')?.target;
  const noPhoto = draft.findings.find((f) => f.status !== 'withdrawn' && f.photos.length === 0);
  const show = (step: HandoverStep, target: ReviewTarget) => {
    if (narrow) navigate(formatStep(location, step), 'push');
    else goTo(target);
  };
  const closingActions: Partial<Record<ClosingProblem, { label: string; run: () => void }>> = {
    ...(firstRoom?.kind === 'room' && {
      items_unchecked: {
        label: 'להמשך הבדיקה',
        run: () => {
          show({ kind: 'room', areaId: firstRoom.areaId }, firstRoom);
        },
      },
    }),
    general_checks_unchecked: {
      label: 'לבדיקות הכלליות',
      run: () => {
        show({ kind: 'checks' }, { kind: 'section', section: 'checks' });
      },
    },
    reinspections_pending: {
      label: 'לבדיקה החוזרת',
      run: () => {
        show({ kind: 'reinspection' }, { kind: 'section', section: 'reinspection' });
      },
    },
    ...(noPhoto !== undefined && {
      findings_without_photo: {
        label: 'להוספת תמונה',
        run: () => {
          setEditing({ mode: 'edit', key: noPhoto.key });
        },
      },
    }),
  };
  const closingPanel = (
    <ClosingPanel
      doc={doc}
      dirty={dirty || findingTyped}
      onChanged={adopt}
      actions={closingActions}
      protocolUrl={
        doc.closing.closed === null
          ? null
          : `/api/projects/${doc.project.id}/handovers/${doc.id}/protocol.pdf`
      }
    />
  );
  const editRooms = () => {
    setEditingRooms(true);
  };
  const openDiscard = () => {
    setDiscarding(true);
  };
  const discardDialog = doc.permissions.discard && (
    <DiscardDraftDialog
      doc={doc}
      unsaved={dirty || findingTyped}
      open={discarding}
      onClose={() => {
        setDiscarding(false);
      }}
      onDiscarded={(next) => {
        // The user chose to lose them: the local copy goes with the draft.
        forgetDraft(next.id);
        forgetOpenFinding(next.id);
        setDiscarding(false);
        onDiscarded(next);
      }}
    />
  );
  const setDate = (value: string) => {
    change((d) => ({ ...d, handoverDate: value }));
  };
  const appointmentText =
    doc.appointment === null
      ? null
      : `${dateOf(doc.appointment.startsAt)} ${timeOf(doc.appointment.startsAt)}`;
  /** The finding dialog is open with something typed that is not in the draft yet. */
  const closeFinding = () => {
    forgetOpenFinding(doc.id);
    setFindingTyped(false);
    setRestoredFields(null);
    setEditing(null);
  };

  const reinspectionPanel =
    doc.reinspection === null ? (
      <p className="empty">למסירה זו אין ליקויים לבדיקה חוזרת.</p>
    ) : (
      <ReinspectionPanel
        reinspection={doc.reinspection}
        draft={draft.reinspections}
        readOnly={readOnly}
        dirty={dirty}
        linking={starting}
        showErrors={showErrors}
        initialPhotoUrl={(photo) =>
          photoUrl(doc.project.id, doc.reinspection?.initial?.id ?? doc.id, photo.id)
        }
        photoUrl={urlOf}
        savedPhotoIds={savedPhotoIds}
        onResult={markReinspection}
        onEdit={setReinspecting}
        onLink={() => {
          const linkable = doc.reinspection?.linkable;
          if (linkable != null) void linkInitial(linkable.id);
        }}
      />
    );
  const checksPanel = (
    <GeneralChecksPanel
      items={doc.generalChecks.items}
      checks={draft.checks}
      readOnly={readOnly}
      showErrors={showErrors}
      starting={starting}
      canStart={!dirty}
      onStart={() => {
        void startChecks();
      }}
      onChange={(id, changes) => {
        change((d) => {
          const before = d.checks[id];
          return before === undefined
            ? d
            : { ...d, checks: { ...d.checks, [id]: { ...before, ...changes } } };
        });
      }}
    />
  );
  const findingsPanel = (
    <Findings
      draft={draft}
      savedPhotoIds={savedPhotoIds}
      readOnly={readOnly}
      catalogs={catalogs}
      urlOf={urlOf}
      onAdd={() => {
        setEditing({ mode: 'add' });
      }}
      onEdit={(key) => {
        setEditing({ mode: 'edit', key });
      }}
      onRemove={(finding) => {
        void askConfirm({
          title: `להסיר את הליקוי "${finding.component}"?`,
          message: 'הליקוי והתמונות שלו יוסרו מהטיוטה בשמירה הבאה.',
          confirmLabel: 'הסרת הליקוי',
          danger: true,
        }).then((yes) => {
          if (yes)
            change((d) => ({ ...d, findings: d.findings.filter((f) => f.key !== finding.key) }));
        });
      }}
      onClose={(finding) => {
        setResolving({ findings: [finding], item: null });
      }}
    />
  );
  const metersPanel = (
    <Meters
      meters={draft.meters}
      readOnly={readOnly}
      showErrors={showErrors}
      onAdd={() => {
        change((d) => ({
          ...d,
          meters: [
            ...d.meters,
            {
              key: newKey(),
              id: null,
              meterType:
                METER_SUGGESTIONS.find((s) => !d.meters.some((m) => m.meterType === s)) ?? '',
              meterNumber: '',
              reading: '',
              note: '',
            },
          ],
        }));
      }}
      onChange={(key, field, value) => {
        change((d) => ({
          ...d,
          meters: d.meters.map((m) => (m.key === key ? { ...m, [field]: value } : m)),
        }));
      }}
      onRemove={(key) => {
        change((d) => ({ ...d, meters: d.meters.filter((m) => m.key !== key) }));
      }}
    />
  );

  const findingInitial =
    restoredFields ??
    editingFinding ??
    (editing?.mode === 'add' && editing.item !== undefined
      ? blankFor(editing.item)
      : editing?.mode === 'add' && editing.room !== undefined
        ? { ...blankFinding, areaId: editing.room.areaId, area: editing.room.area }
        : blankFinding);
  const dialogs = (
    <>
      <Modal
        title="שינויים של משתמש אחר"
        open={merging !== null}
        wide
        onClose={() => {
          if (merging !== null)
            finishMerge(merging, new Set(merging.result.conflicts.map((c) => c.id)));
        }}
      >
        {merging !== null && (
          <MergeForm
            merging={merging}
            onDone={(keepMine) => {
              finishMerge(merging, keepMine);
            }}
          />
        )}
      </Modal>
      <Modal
        title="בדיקה חוזרת"
        open={reinspectedFinding !== undefined}
        onClose={() => {
          discardUploads([]);
          setReinspecting(null);
        }}
      >
        {reinspectedFinding !== undefined && (
          <ReinspectionForm
            key={reinspectedFinding.id}
            title={`${reinspectedFinding.area} · ${reinspectedFinding.component}: ${reinspectedFinding.description}`}
            initial={reinspectionOf(reinspectedFinding.id)}
            uploadPath={photosPath}
            photoUrl={urlOf}
            onUploaded={(photo) => {
              dialogUploads.current.push(photo.id);
            }}
            onCancel={() => {
              discardUploads([]);
              setReinspecting(null);
            }}
            onSubmit={(value) => {
              discardUploads(value.photos);
              setReinspection(reinspectedFinding.id, value);
              setReinspecting(null);
            }}
          />
        )}
      </Modal>
      <Modal
        title={editing?.mode === 'edit' ? 'עריכת ליקוי' : 'הוספת ליקוי'}
        open={editing !== null}
        onClose={() => {
          discardUploads([]);
          closeFinding();
        }}
      >
        {editing !== null && (
          <FindingForm
            key={editing.mode === 'edit' ? editing.key : `add:${editing.item?.id ?? ''}`}
            item={editingItem}
            initial={findingInitial}
            catalogs={catalogs}
            catalogsPath={`/projects/${doc.project.id}/handover-catalogs`}
            uploadPath={photosPath}
            photoUrl={urlOf}
            submitLabel={editing.mode === 'edit' ? 'עדכון הליקוי' : 'הוספה לטיוטה'}
            restored={restoredFields !== null}
            initialFiles={editing.mode === 'add' ? editing.files : undefined}
            onFieldsChange={(fields, changed) => {
              // What is typed survives an expired sign-in or a refresh (this tab only) until it
              // is added to the draft or the dialog is closed.
              setFindingTyped(changed);
              if (changed)
                keepOpenFinding(doc.id, {
                  mode: editing.mode,
                  key: editing.mode === 'edit' ? editing.key : null,
                  itemId: editing.mode === 'add' ? (editing.item?.id ?? null) : null,
                  fields,
                });
              else if (restoredFields === null) forgetOpenFinding(doc.id);
            }}
            onUploaded={(photo) => {
              dialogUploads.current.push(photo.id);
            }}
            onCancel={() => {
              discardUploads([]);
              closeFinding();
            }}
            onSubmit={(fields) => {
              // Uploads removed in the dialog are deleted now; the kept ones attach on save.
              discardUploads(fields.photos);
              const clean = { ...fields, description: fields.description.trim() };
              const item = editing.mode === 'add' ? editing.item : undefined;
              change((d) =>
                editing.mode === 'edit'
                  ? {
                      ...d,
                      findings: d.findings.map((f) =>
                        f.key === editing.key ? { ...f, ...clean } : f,
                      ),
                    }
                  : {
                      ...d,
                      findings: [...d.findings, { key: newKey(), id: null, ...clean }],
                      // The item is "לא תקין" now that it has an open finding.
                      results:
                        item === undefined ? d.results : { ...d.results, [item.id]: 'defect' },
                    },
              );
              if (item !== undefined)
                setNotice(
                  `${item.area} · ${item.name}: נרשם ליקוי, והפריט סומן "${INSPECTION_STATUS_LABELS.defect}".`,
                );
              closeFinding();
            }}
          />
        )}
      </Modal>
      <Modal
        title={
          resolving?.item == null
            ? 'סגירת ליקוי'
            : `סימון "${INSPECTION_STATUS_LABELS[resolving.item.target]}"`
        }
        open={resolving !== null}
        onClose={() => {
          setResolving(null);
        }}
      >
        {resolving !== null && (
          <ResolveForm
            findings={resolving.findings}
            intro={
              resolving.item === null
                ? 'סגירת הליקוי אינה מוחקת אותו. בחרו אם תוקן או נרשם בטעות.'
                : `לפריט "${resolving.item.name}" יש ליקויים פתוחים. כדי לסמן אותו "${INSPECTION_STATUS_LABELS[resolving.item.target]}" יש לסגור אותם: תוקן, או נרשם בטעות.`
            }
            submitLabel={
              resolving.item === null
                ? 'סגירת הליקוי'
                : `סגירה וסימון "${INSPECTION_STATUS_LABELS[resolving.item.target]}"`
            }
            onConfirm={resolve}
            onCancel={() => {
              setResolving(null);
            }}
          />
        )}
      </Modal>
      {/* The phone chooses the date in a sheet; the desktop edits it in the header. */}
      {narrow && (
        <Modal
          title="תאריך מסירה בפועל"
          open={choosingDate}
          onClose={() => {
            setChoosingDate(false);
          }}
        >
          {choosingDate && (
            <DateSheet
              value={draft.handoverDate}
              appointment={appointmentText}
              onSubmit={(value) => {
                setDate(value);
                setChoosingDate(false);
              }}
              onCancel={() => {
                setChoosingDate(false);
              }}
            />
          )}
        </Modal>
      )}
    </>
  );

  if (narrow)
    return (
      <MobileHandover
        doc={doc}
        draft={draft}
        review={review}
        location={location}
        readOnly={readOnly}
        dirty={dirty}
        save={saveView}
        banner={banner}
        notice={notice}
        busy={starting}
        urlOf={urlOf}
        checksView={checksPanel}
        closingView={closingPanel}
        reinspectionView={reinspectionPanel}
        findingsView={
          <>
            {findingsPanel}
            {metersPanel}
          </>
        }
        dialogs={
          <>
            {dialogs}
            {discardDialog}
            {roomsDialog}
          </>
        }
        onLeave={leave}
        onDismissBanner={() => {
          setBanner(null);
        }}
        onRetry={() => {
          void submit(true);
        }}
        onDiscard={discardChanges}
        onDiscardDraft={doc.permissions.discard ? openDiscard : null}
        onEditRooms={readOnly || doc.inspection.templateRevision === null ? null : editRooms}
        onDate={() => {
          setChoosingDate(true);
        }}
        onStartInspection={() => {
          void startInspection();
        }}
        onMark={mark}
        onAllOk={allOk}
        onAddFinding={(item) => {
          setEditing({ mode: 'add', item });
        }}
        onPhotoFinding={
          readOnly
            ? null
            : (room, files) => {
                setEditing({ mode: 'add', room, files });
              }
        }
        onEditFinding={(key) => {
          setEditing({ mode: 'edit', key });
        }}
        onCloseFinding={(finding) => {
          setResolving({ findings: [finding], item: null });
        }}
        onReopenFinding={reopen}
      />
    );

  return (
    <div className={`handover-editor handover-editor--${doc.type}`}>
      <button type="button" className="button button--ghost back-button" onClick={leave}>
        → חזרה לדירה
      </button>
      <section className="protocol-header" aria-label="פרטי הפרוטוקול">
        <div className="protocol-header__main">
          <p className="protocol-header__type">
            <span className={`type-dot type-dot--${doc.type}`} aria-hidden="true" />
            {HANDOVER_TYPE_LABELS[doc.type]}
          </p>
          <h2 className="protocol-header__title">{apartmentTitle(doc.apartment)}</h2>
          <p className="protocol-header__meta">
            <span>
              פרוטוקול <Value text={doc.protocolNumber} />
            </span>
            <span className={`chip chip--${doc.status}`}>{HANDOVER_STATUS_LABELS[doc.status]}</span>
            {readOnly && <span className="chip">צפייה בלבד</span>}
          </p>
          <p className="protocol-header__appointment" aria-label="מועד ושיבוץ">
            {doc.appointment === null ? (
              <span className="hint">טרם נקבע מועד ביומן המסירות</span>
            ) : (
              <>
                <span>מועד ביומן: {appointmentText}</span>
                <span>אחראי: {doc.appointment.primary ?? 'טרם שובץ'}</span>
                {doc.appointment.participants.length > 0 && (
                  <span>מלווים: {doc.appointment.participants.join(', ')}</span>
                )}
              </>
            )}
          </p>
        </div>
        <div className="protocol-header__date">
          <label className="field" htmlFor={dateId}>
            <span>תאריך מסירה בפועל</span>
            <input
              id={dateId}
              type="date"
              value={draft.handoverDate}
              disabled={readOnly}
              aria-describedby={isFuture(draft.handoverDate) ? `${dateId}-future` : undefined}
              onChange={(e) => {
                setDate(e.target.value);
              }}
            />
          </label>
          {isFuture(draft.handoverDate) && <FutureDateWarning id={`${dateId}-future`} />}
        </div>
      </section>

      <PropertySummary
        projectId={doc.project.id}
        projectName={doc.project.name}
        apartment={doc.apartment}
      />

      {banner !== null && (
        <div className="notice notice--info draft-banner" role="status">
          <p>{banner}</p>
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              setBanner(null);
            }}
          >
            הבנתי
          </button>
        </div>
      )}

      <div className="review-bar">
        <p className="hint">
          {review.complete
            ? 'הבדיקה הושלמה.'
            : `טרם נבדקו ${String(review.incomplete.length)}${
                doc.type === 'final'
                  ? ` · ממתינים לבדיקה חוזרת ${String(review.awaiting.length)}`
                  : ''
              } · מידע חסר ${String(review.missing.length)}`}
        </p>
        <button
          type="button"
          className="button"
          onClick={() => {
            setReviewing(true);
          }}
        >
          סיכום הבדיקה
        </button>
      </div>

      {doc.reinspection !== null && reinspectionPanel}

      {!readOnly && doc.inspection.templateRevision !== null && (
        <div className="rooms-entry">
          <button type="button" className="button" onClick={editRooms}>
            <Icon name="layers" /> חדרים בדירה
          </button>
          {doc.inspection.excludedRooms.length > 0 && (
            <span className="hint">
              הוסרו מהמסירה: {doc.inspection.excludedRooms.map((r) => r.area).join(', ')}
            </span>
          )}
        </div>
      )}
      {items.length > 0 || doc.inspection.templateRevision !== null ? (
        <InspectionPanel
          items={items}
          results={draft.results}
          findings={draft.findings}
          readOnly={readOnly}
          notice={notice}
          onMark={mark}
          onAllOk={allOk}
          onAddFinding={(item) => {
            setEditing({ mode: 'add', item });
          }}
          onEditFinding={(key) => {
            setEditing({ mode: 'edit', key });
          }}
          onCloseFinding={(finding) => {
            setResolving({ findings: [finding], item: null });
          }}
          onReopenFinding={reopen}
          focusRoom={focusRoom}
        />
      ) : (
        <section className="editor-section inspection-start" aria-label="בדיקת הדירה">
          <h2 className="section-title">בדיקת הדירה</h2>
          <p className="hint">
            לטיוטה זו עדיין אין רשימת בדיקה מסודרת לפי חדרים. הליקויים שנרשמו בה נשמרים כפי שהם.
          </p>
          {!readOnly && (
            <>
              <button
                type="button"
                className="button button--primary"
                disabled={dirty || starting}
                onClick={() => {
                  void startInspection();
                }}
              >
                {starting ? 'מתחיל…' : 'התחלת בדיקה לפי תבנית הפרויקט'}
              </button>
              {dirty && <p className="hint">יש לשמור את הטיוטה לפני התחלת הבדיקה.</p>}
            </>
          )}
        </section>
      )}

      {checksPanel}
      {findingsPanel}
      {metersPanel}

      <Modal
        title="סיכום הבדיקה"
        open={reviewing}
        wide
        onClose={() => {
          setReviewing(false);
        }}
      >
        {reviewing && <InspectionReview review={review} type={doc.type} onGo={goTo} />}
      </Modal>

      {closingPanel}

      {!readOnly && (
        <div className="save-bar" role="region" aria-label="שמירת הטיוטה">
          {save.kind === 'error' && (
            <p className="notice notice--error save-bar__notice" role="alert">
              {save.text}
            </p>
          )}
          {!valid && dirty && showErrors && mismatched.length > 0 && (
            <p className="notice notice--error save-bar__notice" role="alert">
              תוצאת הבדיקה אינה תואמת את הליקויים בפריטים: {mismatched.map(itemName).join(', ')}.
            </p>
          )}
          <p
            className={`save-bar__status save-bar__status--${statusKind}`}
            role="status"
            data-state={statusKind}
          >
            <span className="save-bar__dot" aria-hidden="true" />
            {status}
          </p>
          <div className="save-bar__actions">
            {doc.permissions.discard && (
              <button
                type="button"
                className="button button--danger-quiet save-bar__discard"
                disabled={save.kind === 'saving'}
                onClick={openDiscard}
              >
                מחיקת הטיוטה
              </button>
            )}
            <button
              type="button"
              className="button"
              disabled={!dirty || save.kind === 'saving'}
              onClick={discardChanges}
            >
              ביטול שינויים
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={!dirty || save.kind === 'saving' || merging !== null}
              onClick={() => {
                void submit(true);
              }}
            >
              {save.kind === 'offline' || save.kind === 'error'
                ? 'ניסיון שמירה חוזר'
                : 'שמירה עכשיו'}
            </button>
          </div>
        </div>
      )}

      {dialogs}
      {discardDialog}
      {roomsDialog}
    </div>
  );
}

/** One handover protocol draft: the same editor for the initial and the final handover. */
export function HandoverEditor({
  location,
  onBack,
}: {
  /** Where the handover is (its project, apartment and id). */
  readonly location: HandoverLocation & { readonly projectId: string; readonly handoverId: string };
  readonly onBack: () => void;
}) {
  const { projectId, handoverId } = location;
  /** Discarded in this editor just now (the loaded document is still the draft). */
  const [discarded, setDiscarded] = useState<HandoverDocument | null>(null);
  const [keptLocally, setKeptLocally] = useState(() => findDraft(handoverId) !== null);
  const [data, reload] = useLoad(`handover:${handoverId}`, () =>
    apiGet(`/projects/${projectId}/handovers/${handoverId}`, handoverDocumentSchema),
  );
  const [catalogs, reloadCatalogs] = useLoad(`handover-catalogs:${projectId}`, () =>
    apiGet(`/projects/${projectId}/handover-catalogs`, handoverCatalogsResponseSchema),
  );
  if (catalogs.state === 'loading') return <LoadingBlock />;
  if (catalogs.state !== 'ready')
    return (
      <Problem
        text="טעינת רשימות האזורים, הרכיבים והקבלנים נכשלה."
        requestId={catalogs.state === 'error' ? catalogs.requestId : null}
        onRetry={reloadCatalogs}
      />
    );
  if (data.state === 'loading') return <LoadingBlock />;
  if (data.state === 'forbidden')
    return (
      <Problem
        text="המסירה לא נמצאה או שאין לך הרשאה לצפות בה."
        onRetry={onBack}
        actionLabel="חזרה"
      />
    );
  if (data.state === 'error')
    return <Problem text="טעינת המסירה נכשלה." requestId={data.requestId} onRetry={reload} />;
  const gone = discarded ?? (data.data.status === 'discarded' ? data.data : null);
  if (gone !== null)
    return (
      <DiscardedNotice
        doc={gone}
        keptLocally={discarded === null && keptLocally}
        onForgetLocal={() => {
          void askConfirm({
            title: 'למחוק את השינויים מהמכשיר?',
            message:
              'השינויים לא נשמרו בשרת, והטיוטה שאליה שייכים נמחקה. אחרי המחיקה לא ניתן לשחזר אותם.',
            confirmLabel: 'מחיקת השינויים',
            danger: true,
          }).then((yes) => {
            if (!yes) return;
            forgetDraft(gone.id);
            forgetOpenFinding(gone.id);
            setKeptLocally(false);
          });
        }}
        onBack={onBack}
      />
    );
  // The editor keeps the document it saves; it is created once per handover.
  return (
    <EditorBody
      key={data.data.id}
      initialDoc={data.data}
      catalogs={catalogs.data}
      location={location}
      onBack={onBack}
      onDiscarded={setDiscarded}
    />
  );
}
